/**
 * The navigation map.
 *
 * One definition shared by the desktop rail, the mobile bar and the command
 * palette, so a new page cannot appear in one and not the others.
 *
 * Order is deliberate, and it follows the owner's own sequence of work
 * (2026-09-03): Markets and Backtest are where a strategy is found and
 * measured, Deploy is where it is sent to a market, and Monitoring sits
 * directly under Deploy because watching it run is the next thing you do.
 * Monitoring used to sit under Dashboard, on the argument that "is anything
 * exposed right now" must always be one click away; the Dashboard now carries
 * the execution truth chain itself, so that question is answered before the
 * rail is used at all.
 */

import {
  Activity,
  CandlestickChart,
  Database,
  BookOpenCheck,
  FlaskConical,
  LayoutDashboard,
  Settings,
  Wallet,
  Workflow,
  type LucideIcon,
} from 'lucide-react';

import { demoNamesActive } from '@/lib/demoNames';

export interface NavItem {
  to: string;
  label: string;
  /** Shown in the mobile bar, where horizontal space is scarce. */
  shortLabel: string;
  icon: LucideIcon;
  /** Bound in AppShell as `g` then this key. */
  hotkey: string;
  description: string;
  /** Appears in the 5-slot mobile nav bar. */
  mobile: boolean;
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    to: '/',
    label: 'Dashboard',
    shortLabel: 'Home',
    icon: LayoutDashboard,
    hotkey: 'd',
    description: 'Open risk, equity and what the machine is doing right now',
    mobile: true,
  },
  {
    to: '/markets',
    label: 'Markets',
    shortLabel: 'Chart',
    icon: CandlestickChart,
    hotkey: 'm',
    description: 'Charts, indicators, drawings and stored history',
    mobile: true,
  },
  {
    to: '/backtest',
    label: 'Backtest',
    shortLabel: 'Test',
    // The flask matches the Documentation's own icon for the Backtesting section,
    // so the tab and its guide read as the same concept.
    icon: FlaskConical,
    hotkey: 'b',
    description: 'Runs, the run registry, comparison and parameter sweeps',
    mobile: true,
  },
  {
    to: '/strategies',
    label: 'Deploy',
    shortLabel: 'Deploy',
    icon: Workflow,
    /*
      `s` is kept even though the tab is now called Deploy: `d` belongs to
      Dashboard, and AppShell resolves a duplicate with `.find()` — the second
      of two identical hotkeys is silently unreachable from the keyboard.
    */
    hotkey: 's',
    description: 'Choose a strategy, read its evidence, and deploy it to a market',
    mobile: true,
  },
  {
    to: '/monitoring',
    label: 'Monitoring',
    shortLabel: 'Live',
    icon: Activity,
    hotkey: 'l',
    description: 'Deployments, positions, stops and the kill-switch',
    mobile: true,
  },
  {
    to: '/portfolio',
    label: 'Portfolio',
    shortLabel: 'P&L',
    icon: Wallet,
    hotkey: 'p',
    description: 'Trades, attribution, excursion and performance',
    mobile: false,
  },
  {
    to: '/settings',
    label: 'Settings',
    shortLabel: 'Setup',
    icon: Settings,
    hotkey: ',',
    description: 'Environment, alert channels, theme and data quality',
    mobile: false,
  },
  {
    to: '/database',
    label: 'Database',
    shortLabel: 'Data',
    icon: Database,
    /*
      `a` because d/l/m/b/s/p/, are taken above and g/t/Shift+H are reserved
      globally in AppShell. A duplicate is validated nowhere — AppShell does
      `.find()` and returns the first match, so a collision would silently
      make one of the two tabs unreachable from the keyboard.
    */
    hotkey: 'a',
    description: 'Every table the platform has written, browsable and filterable',
    /*
      `false` is MANDATORY, not a preference. MOBILE_NAV_ITEMS below feeds a
      bar whose docstring pins it at five slots, and there are already exactly
      five `mobile: true` entries. A sixth does not overflow — it silently
      narrows all of them.
    */
    mobile: false,
  },
  {
    to: '/documentation',
    label: 'Documentation',
    shortLabel: 'Guide',
    icon: BookOpenCheck,
    hotkey: 'h',
    description: 'How data, testing, deployment and monitoring work together',
    mobile: false,
  },
] as const;

/** The tabs offered in the menus. Demo mode (lib/demoNames) hides Database and Documentation. */
export const SHOWN_NAV_ITEMS: readonly NavItem[] = demoNamesActive()
  ? NAV_ITEMS.filter((item) => item.to !== '/database' && item.to !== '/documentation')
  : NAV_ITEMS;

export const MOBILE_NAV_ITEMS = NAV_ITEMS.filter((item) => item.mobile);

/** Longest matching prefix, so `/backtest/42` still highlights Backtest. */
export function activeNavItem(pathname: string): NavItem | undefined {
  return NAV_ITEMS.filter(
    (item) => item.to === '/' ? pathname === '/' : pathname.startsWith(item.to),
  ).sort((a, b) => b.to.length - a.to.length)[0];
}
